import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { PexIcon } from '../../../components/icons/PexIcon'
import { formatCurrency } from '../../../lib/currency'
import { PRICE_MASK, usePricesHidden } from '../../../lib/price-mask'
import { cn } from '../../../lib/utils'
import { CatalogArtwork } from './CatalogArtwork'
import { catalogArtworkSurface } from './catalog-artwork-style'
import { formatCatalogCategory } from './SellerCatalogProductCard.lib'

export type SellerCatalogCardDensity = 'guided' | 'scan'

export type SellerCatalogProductCardProps = {
  productId: string
  routeId?: string
  name: string
  category: string
  coverImageUrl: string | null
  hoverImageUrl?: string | null
  priceUsd: number | null
  label?: string | null
  favorite: boolean
  facts?: string[]
  density?: SellerCatalogCardDensity
  onToggleFavorite: (id: string) => void
  unavailableLabel?: string
  priceLabel?: string
  favoriteLabel?: string
  removeFavoriteLabel?: string
}

export function SellerCatalogProductCard({
  productId,
  routeId = productId,
  name,
  category,
  coverImageUrl,
  hoverImageUrl = null,
  priceUsd,
  label = null,
  favorite,
  facts = [],
  density = 'guided',
  onToggleFavorite,
  unavailableLabel = 'Unavailable',
  priceLabel = 'Your price',
  favoriteLabel = `Add ${name} to favorites`,
  removeFavoriteLabel = `Remove ${name} from favorites`,
}: SellerCatalogProductCardProps) {
  const [coverBroken, setCoverBroken] = useState(false)
  const pricesHidden = usePricesHidden()
  const [hoverBroken, setHoverBroken] = useState(false)
  const categoryLabel = formatCatalogCategory(category)
  const factsText = facts.filter(Boolean).slice(0, 3).join(' \u00b7 ')

  return (
    <article
      data-testid="seller-catalog-product-card"
      data-density={density}
      className="group relative min-w-0 overflow-hidden rounded-[var(--catalog-card-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-hairline)] transition-[transform,box-shadow,border-color] duration-300 ease-[var(--ease-mass)] hover:-translate-y-0.5 hover:border-[var(--color-border-strong)] hover:shadow-[var(--shadow-lifted)]"
    >
      <Link
        to="/seller/catalog/$productId"
        params={{ productId: routeId }}
        aria-label={name}
        className="block focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_3px_var(--color-focus)]"
      >
        <div
          className={cn(
            'relative overflow-hidden',
            density === 'guided' ? 'aspect-[4/3]' : 'aspect-[5/4]',
            catalogArtworkSurface(category),
          )}
        >
          {coverImageUrl && !coverBroken ? (
            <>
              <img
                src={coverImageUrl}
                alt={name}
                loading="lazy"
                onError={() => setCoverBroken(true)}
                className="h-full w-full object-cover transition-transform duration-500 ease-[var(--ease-mass)] group-hover:scale-[1.018]"
              />
              {hoverImageUrl && !hoverBroken && (
                <img
                  src={hoverImageUrl}
                  alt=""
                  loading="lazy"
                  onError={() => setHoverBroken(true)}
                  className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-300 ease-[var(--ease-mass)] group-hover:opacity-100"
                />
              )}
            </>
          ) : (
            <CatalogArtwork
              category={category}
              className="absolute inset-x-[14%] bottom-[-3%] h-[88%] w-[72%] transition-transform duration-500 ease-[var(--ease-mass)] group-hover:scale-[1.018]"
            />
          )}
          {label && (
            <span className="absolute left-3 top-3 rounded-[6px] bg-[var(--catalog-card-badge-bg)] px-2 py-1 text-[8px] font-semibold uppercase tracking-[0.05em] text-[var(--catalog-card-badge-fg)]">
              {label}
            </span>
          )}
        </div>

        <div className={cn('px-3 pb-3', density === 'guided' ? 'pt-3.5' : 'pt-3')}>
          <h3 className="line-clamp-2 min-h-[2.25rem] text-[length:var(--catalog-card-title-size)] font-[var(--catalog-card-title-weight)] leading-[1.35] tracking-[-0.01em] text-[var(--color-text)]">
            {name}
          </h3>
          <p className="mt-1.5 truncate text-[length:var(--catalog-card-meta-size)] font-medium uppercase tracking-[0.045em] text-[var(--color-text-muted)]">
            {categoryLabel}
          </p>
          <div className="mt-3 border-t border-[var(--color-border)] pt-2.5">
            <span className="block text-[9px] font-semibold uppercase tracking-[0.07em] text-[var(--color-text-subtle)]">
              {priceLabel}
            </span>
            <strong className="mt-0.5 block text-[length:var(--catalog-card-price-size)] font-semibold tabular-nums text-[var(--color-text)]">
              {priceUsd == null ? (pricesHidden ? PRICE_MASK : unavailableLabel) : `From ${formatCurrency(priceUsd, 'USD')}`}
            </strong>
          </div>
          <p className="mt-2 min-h-4 truncate text-[length:var(--catalog-card-meta-size)] text-[var(--color-text-muted)]">
            {factsText}
          </p>
        </div>
      </Link>

      <button
        type="button"
        aria-label={favorite ? removeFavoriteLabel : favoriteLabel}
        aria-pressed={favorite}
        onClick={() => onToggleFavorite(productId)}
        className="absolute right-2.5 top-2.5 flex size-11 items-center justify-center rounded-[var(--radius-control)] border border-black/[0.06] bg-white/95 text-[var(--color-text-muted)] shadow-[0_2px_8px_rgb(20_25_23/0.07)] transition-[transform,color,background-color] duration-300 ease-[var(--ease-mass)] hover:-translate-y-px hover:bg-white hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] motion-reduce:transition-none"
      >
        <PexIcon
          name="favorite"
          size="sm"
          weight={favorite ? 'duotone' : 'regular'}
          tone={favorite ? 'signal' : 'muted'}
          decorative
        />
      </button>
    </article>
  )
}
